/**
 * Whether the banks that run an MCE adapter can report its faults.
 *
 * VTS reads an adapter's event mapping, but it listens for MCE events only on the
 * bank's `event_reporting_address` (`Archive.Bank`). A bank with an event mapping
 * and no address gets no faults, and nothing on the adapter says so.
 */
import { formatUuidT } from "../../flatbuffers/display";
import { UuidT } from "../../flatbuffers/generated/front-end/uuid";

/** What the adapter editor can say about the banks' addresses. */
export type EventAddressStatus =
  | { state: "unknown" }
  | { state: "no-banks" }
  | { state: "ok" }
  | { state: "missing"; banks: string[] };

/** A bank as a list response gives it, FlatBuffer-decoded or JSON. */
export interface BankAddressRow {
  name?: string | Uint8Array | null;
  adapterId?: UuidT | string | null;
  adapter_id?: string | null;
  eventReportingAddress?: string | Uint8Array | null;
  event_reporting_address?: string | null;
}

function text(value: string | Uint8Array | null | undefined): string {
  if (value == null) return "";
  return typeof value === "string" ? value : new TextDecoder().decode(value);
}

function adapterIdOf(bank: BankAddressRow): string {
  const raw = bank.adapterId ?? bank.adapter_id;
  return typeof raw === "string" ? raw.toLowerCase() : formatUuidT(raw).toLowerCase();
}

/** The banks that use `adapterId`, by name, split by whether they name an event reporting address. */
export function eventAddressStatusOf(
  banks: BankAddressRow[],
  adapterId: string,
): EventAddressStatus {
  const mine = banks.filter((bank) => adapterIdOf(bank) === adapterId.toLowerCase());
  if (mine.length === 0) return { state: "no-banks" };
  const without = mine
    .filter(
      (bank) => text(bank.eventReportingAddress ?? bank.event_reporting_address).trim() === "",
    )
    .map((bank) => text(bank.name) || "(unnamed bank)");
  return without.length > 0 ? { state: "missing", banks: without } : { state: "ok" };
}
